import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import type { ColDef } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DollarSign } from 'lucide-react';

import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import type {
	PricingPoliciesQuery,
	PricingPolicyQuery
} from '@/graphql/generated';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';

import { StatusBadge } from '@/components/ui/status-badge';
import {
	PRICING_POLICIES_LIST_QUERY,
	PRICING_POLICY_QUERY,
	PRICING_POLICY_DELETE_MUTATION
} from './pricing-policies.gql.tsx';
import { PricingPolicyDetails } from './pricing-policy-details';

type PricingPolicyListNode = NonNullable<
	NonNullable<PricingPoliciesQuery['pricingPolicies']>['nodes']
>[number];

interface PricingPoliciesListResponse {
	pricingPolicies?: {
		nodes?: PricingPolicyListNode[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

// Helper component to avoid lint warnings
const PricingPolicyStatusBadge = (policy: PricingPolicyListNode) => (
	<StatusBadge isActive={policy.isActive} />
);

const PricingPolicyEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0 ring-2 ring-white ring-offset-0">
		<DollarSign className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const PricingPoliciesList = () => {
	const { t } = useTranslation('pricingPolicies');
	const [selectedPolicy, setSelectedPolicy] =
		useState<PricingPolicyListNode | null>(null);
	const [localPolicies, setLocalPolicies] = useState<PricingPolicyListNode[]>(
		[]
	);
	const { showDialog } = useConfirmationDialog();
	const [searchParams, setSearchParams] = useSearchParams();
	const { goToCreate, goToEdit } = useResourceNavigation('pricingPolicies');
	const go = useGo();

	// Track total items to fetch (for preserving count after create/edit refetch)
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const { data, loading: isLoading, refetch, fetchMore } =
		useQuery<PricingPoliciesListResponse>(PRICING_POLICIES_LIST_QUERY, {
			variables: {
				first: itemsToFetch
			}
		});

	// Pagination state
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.pricingPolicies?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.pricingPolicies?.pageInfo?.endCursor;

	// Load more handler using fetchMore
	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.pricingPolicies) return prev;

					return {
						...prev,
						pricingPolicies: {
							...fetchMoreResult.pricingPolicies,
							nodes: [
								...(prev.pricingPolicies?.nodes ?? []),
								...(fetchMoreResult.pricingPolicies.nodes ?? [])
							]
						}
					};
				}
			});
			// Reset local policies to use server data after fetchMore
			setLocalPolicies([]);
		} catch (error) {
			console.error('Error loading more pricing policies:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor]);

	const pricingPoliciesData = data?.pricingPolicies?.nodes ?? [];

	const handleRefresh = () => {
		setLocalPolicies([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({ first: DEFAULT_PAGE_SIZE });
	};

	// Lazy query for fetching individual pricing policy after success
	const [fetchPricingPolicy] =
		useLazyQuery<PricingPolicyQuery>(PRICING_POLICY_QUERY);

	// Delete mutation
	const [deletePricingPolicy, { loading: deleteLoading }] = useMutation(
		PRICING_POLICY_DELETE_MUTATION
	);

	// Combined data: server data + local modifications
	const allPolicies = useMemo(() => {
		const serverPolicies = pricingPoliciesData || [];

		// Always prefer server data when empty - local state is only for optimistic updates
		if (localPolicies.length === 0 || serverPolicies.length === 0) {
			return serverPolicies;
		}

		// Merge local policies with server data
		const mergedPolicies = [...serverPolicies];

		for (const localPolicy of localPolicies) {
			const existingIndex = mergedPolicies.findIndex(
				policy => policy.pricingPolicyId === localPolicy.pricingPolicyId
			);

			if (existingIndex >= 0) {
				// Replace existing (for edits)
				mergedPolicies[existingIndex] = localPolicy;
			} else {
				// Add new (for creates)
				mergedPolicies.unshift(localPolicy);
			}
		}

		return mergedPolicies;
	}, [pricingPoliciesData, localPolicies]);

	// Handle success from create/edit operations
	const handlePolicySuccess = useCallback(
		async (pricingPolicyId: string) => {
			try {
				// Fetch the updated/created pricing policy from server
				const { data: policyData } = await fetchPricingPolicy({
					variables: { id: pricingPolicyId },
					fetchPolicy: 'network-only' // Always fetch fresh data
				});

				const policyFromServer = policyData?.pricingPolicy;
				if (!policyFromServer) {
					console.warn(
						'Could not fetch pricing policy data after success'
					);
					return;
				}

				// Map the detailed policy data to list format
				const listPolicy: PricingPolicyListNode = {
					pricingPolicyId: policyFromServer.pricingPolicyId,
					name: policyFromServer.name,
					priority: policyFromServer.priority,
					isActive: policyFromServer.isActive,
					scenario: policyFromServer.scenario,
					tariff: policyFromServer.tariff,
					overstayTariff: policyFromServer.overstayTariff,
					limit: policyFromServer.limit,
					billingCycle: policyFromServer.billingCycle
				};

				// Update local policies
				setLocalPolicies(prevLocal => {
					const existingIndex = prevLocal.findIndex(
						policy => policy.pricingPolicyId === pricingPolicyId
					);

					if (existingIndex >= 0) {
						// Replace existing policy (edit case)
						const updated = [...prevLocal];
						updated[existingIndex] = listPolicy;
						return updated;
					} else {
						// Add new policy (create case)
						return [listPolicy, ...prevLocal];
					}
				});

				// Select and highlight the policy
				setSelectedPolicy(listPolicy);
			} catch (error) {
				console.error(
					'Error fetching pricing policy after success:',
					error
				);
			}
		},
		[fetchPricingPolicy]
	);

	// URL parameter handling for success operations
	useEffect(() => {
		const successPolicyId = searchParams.get('success');

		if (successPolicyId) {
			// Clear the URL parameters
			setSearchParams(params => {
				params.delete('success');
				params.delete('mode');
				return params;
			});

			// Handle the success
			handlePolicySuccess(successPolicyId);
		}
	}, [searchParams, setSearchParams, handlePolicySuccess]);

	const handleRowClick = (policy: PricingPolicyListNode) => {
		setSelectedPolicy(policy);
	};

	// Helper function to find the next pricing policy to select after deletion
	const findNextPolicyToSelect = (
		deletedPolicy: PricingPolicyListNode,
		policyList: PricingPolicyListNode[]
	) => {
		const currentIndex = policyList.findIndex(
			p => p.pricingPolicyId === deletedPolicy.pricingPolicyId
		);

		if (policyList.length <= 1) {
			return null;
		}

		// Try to select the next policy (same index after deletion)
		if (currentIndex < policyList.length - 1) {
			return policyList[currentIndex + 1];
		}

		// If deleting the last policy, select the previous one
		if (currentIndex > 0) {
			return policyList[currentIndex - 1];
		}

		return null;
	};

	// Helper function to handle post-deletion selection
	const handlePostDeletionSelection = (
		nextPolicyToSelect: PricingPolicyListNode | null,
		updatedPolicies: PricingPolicyListNode[]
	) => {
		setLocalPolicies([]);
		// First, clear the current selection completely
		setSelectedPolicy(null);

		// Then after a brief delay, set the next policy
		setTimeout(() => {
			if (nextPolicyToSelect && updatedPolicies.length > 0) {
				// Find the policy in the updated list by ID
				const updatedPolicy = updatedPolicies.find(
					p => p.pricingPolicyId === nextPolicyToSelect?.pricingPolicyId
				);

				if (updatedPolicy) {
					setSelectedPolicy(updatedPolicy);
				}
			}
		}, 100);
	};

	const handleDeletePricingPolicy = async (policy: PricingPolicyListNode) => {
		const confirmed = await showDialog(
			'Delete Pricing Policy',
			`Are you sure you want to delete pricing policy "${policy.name}"? This action cannot be undone.`
		);

		if (confirmed) {
			// Determine if we need to find the next policy to select
			const wasSelectedPolicy =
				selectedPolicy?.pricingPolicyId === policy.pricingPolicyId;

			// Clear selection immediately if deleting the selected policy
			if (wasSelectedPolicy) {
				setSelectedPolicy(null);
			}

			try {
				const result = await deletePricingPolicy({
					variables: {
						input: {
							pricingPolicyId: policy.pricingPolicyId
						}
					},
					refetchQueries: ['PricingPolicies']
				});

				const checkResult = result.data?.deletePricingPolicy;

				if (checkResult?.success) {
					showMutationSuccessToast('pricingPolicies', 'delete');

					// Find the next policy to select after deletion (before refetch)
					let nextPolicyToSelect = null;
					if (wasSelectedPolicy) {
						nextPolicyToSelect = findNextPolicyToSelect(
							policy,
							allPolicies
						);
					}

					// Refetch data
					refetch();

					// Wait a bit for the refresh to complete, then handle selection
					setTimeout(() => {
						handlePostDeletionSelection(
							nextPolicyToSelect,
							pricingPoliciesData || []
						);
					}, 200);
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(
						`Failed to delete pricing policy: ${errorMessage}`
					);
				}
			} catch (error) {
				console.error('Delete pricing policy error:', error);
				toast.error('Failed to delete pricing policy. Please try again.');
			}
		}
	};

	const handleDeletePricingPolicyById = async (pricingPolicyId: string) => {
		const policy = pricingPoliciesData?.find(
			p => p.pricingPolicyId === pricingPolicyId
		);
		if (policy) {
			await handleDeletePricingPolicy(policy);
		}
	};

	const handleClonePricingPolicy = (pricingPolicyId: string) => {
		const policy = pricingPoliciesData?.find(
			p => p.pricingPolicyId === pricingPolicyId
		);
		if (policy) {
			go({
				to: '/pricing-policies/create',
				state: {
					cloneData: {
						// Core fields
						name: `${policy.name} (Copy)`,
						priority: policy.priority,
						isActive: policy.isActive,
						// Foreign keys with their labels for autocomplete display
						scenarioId: policy.scenario?.scenarioId,
						scenarioLabel: policy.scenario?.name,
						tariffId: policy.tariff?.tariffId,
						tariffLabel: policy.tariff?.name,
						overstayTariffId: policy.overstayTariff?.tariffId,
						overstayTariffLabel: policy.overstayTariff?.name,
						limitId: policy.limit?.limitId,
						limitLabel: policy.limit?.name,
						billingCycleId: policy.billingCycle?.billingCycleId
					}
				}
			});
		}
	};

	const columns: ColDef<PricingPolicyListNode>[] = useMemo(
		() => [
			{
				headerName: t('list.columns.name'),
				field: 'name',
				flex: 1,
				minWidth: 200
			},
			{
				headerName: t('list.columns.scenario'),
				colId: 'scenarioDescription',
				flex: 1,
				minWidth: 160,
				valueGetter: params => params.data?.scenario?.name ?? '—'
			},
			{
				headerName: t('list.columns.primaryTariff'),
				colId: 'tariffName',
				flex: 1,
				minWidth: 160,
				valueGetter: params => params.data?.tariff?.name ?? '—'
			},
			{
				headerName: t('list.columns.overstayTariff'),
				colId: 'overstayTariffName',
				flex: 1,
				minWidth: 160,
				valueGetter: params => params.data?.overstayTariff?.name ?? '—'
			},
			{
				headerName: t('list.columns.limit'),
				colId: 'limitDescription',
				flex: 1,
				minWidth: 160,
				valueGetter: params => params.data?.limit?.name ?? '—'
			},
			{
				headerName: t('list.columns.priority'),
				field: 'priority',
				width: 110,
				minWidth: 110,
				cellClass: 'ag-right-aligned-cell',
				headerClass: 'ag-right-aligned-header',
				valueFormatter: params => `${params.value ?? ''}`
			},
			{
				headerName: t('list.columns.status'),
				field: 'isActive',
				width: 120,
				minWidth: 120,
				cellRenderer: ActiveInactiveCellRenderer
			},
			{
				headerName: '',
				field: undefined,
				width: 60,
				minWidth: 60,
				maxWidth: 60,
				cellRenderer: DeleteCellRenderer,
				cellRendererParams: {
					onDelete: handleDeletePricingPolicy,
					disabled: deleteLoading
				},
				sortable: false,
				filter: false,
				resizable: false
			}
		],
		[t, handleDeletePricingPolicy, deleteLoading]
	);

	return (
		<ListPageLayout
			columns={columns}
			data={allPolicies}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={isLoading || deleteLoading}
			onRowClick={handleRowClick}
			onCreate={goToCreate}
			onRefresh={handleRefresh}
			selectedItem={selectedPolicy}
			detailsTitle="Pricing Policy Details"
			onEdit={goToEdit}
			onDelete={handleDeletePricingPolicyById}
			onClone={handleClonePricingPolicy}
			onOpenInNewWindow={id => openEntityInNewWindow('pricingPolicies', id)}
			renderDetailsContent={(policy: PricingPolicyListNode) => (
				<PricingPolicyDetails
					pricingPolicyId={policy.pricingPolicyId}
				/>
			)}
			getRecordId={policy => policy.pricingPolicyId}
			getDetailsDescription={policy => policy.name}
			getStatusBadge={PricingPolicyStatusBadge}
			renderEntityIcon={() => <PricingPolicyEntityIcon />}
			getEntityTypeName={policy =>
				policy.isActive ? t('details.activePolicy') : t('details.inactivePolicy')
			}
			showCreateButton={true}
			showRefreshButton={true}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
};
